import { apiClient } from '@/services/apiClient'
import { SunlightData } from '@popety-io/types'

/**
 * Fetch sunlight statistics for a land property
 */
export const getSunlightStatisticsForLand = async (
  landId: string,
  coordinates?: { lat: number; lon: number }
): Promise<SunlightData> => {
  // Frontend-only solution using SunCalc
  // Import the calculator function
  const { calculateSunlightData } = await import('./sunlightCalculator')

  // If no coordinates provided, we'll need to get them from the land data
  // This will be handled in the component
  if (!coordinates) {
    throw new Error('Coordinates are required for sunlight calculations')
  }

  return calculateSunlightData(coordinates)
}

/**
 * Fetch sunlight statistics for a building
 */
export const getSunlightStatisticsForBuilding = async (
  buildingId: string
): Promise<SunlightData> => {
  try {
    const response = await apiClient.get(
      `/sunlight/statistics/building/${buildingId}`
    )
    return response.data
  } catch (error) {
    console.error('Error fetching sunlight statistics for building:', error)
    throw error
  }
}

/**
 * Fetch sunlight statistics for a custom polygon
 */
export const getSunlightStatisticsForPolygon = async (
  polygon: Array<{ lat: number; lon: number }>
): Promise<SunlightData> => {
  try {
    const response = await apiClient.post('/sunlight/statistics/polygon', {
      polygon,
    })
    return response.data
  } catch (error) {
    console.error('Error fetching sunlight statistics for polygon:', error)
    throw error
  }
}

/**
 * Fetch sunlight data for coordinates (creates a small buffer around the point)
 */
export const fetchSunlightData = async (params: {
  latitude: number
  longitude: number
}): Promise<SunlightData> => {
  try {
    // Create a small square polygon around the point (approximately 100m x 100m)
    const offset = 0.0005 // Approximately 50 meters in degrees
    const polygon = [
      { lat: params.latitude - offset, lon: params.longitude - offset },
      { lat: params.latitude + offset, lon: params.longitude - offset },
      { lat: params.latitude + offset, lon: params.longitude + offset },
      { lat: params.latitude - offset, lon: params.longitude + offset },
    ]

    return await getSunlightStatisticsForPolygon(polygon)
  } catch (error) {
    console.error('Error fetching sunlight data:', error)
    throw error
  }
}
